/**
 * 监控 Ajax 请求
 * 此模块是对 src/network.js 的重构，新增了监控 fetch 的能力
 * 实现原理：改写 window.XMLHttpRequest.prototype.open 和 window.fetch
 */

import { getPageViewSize } from './util/screen';
import { commitid, gitOriginUrl } from './constants';
import { Data, LogOptions, LogState } from './types';

/**
 * 改写 XMLHttpRequest 和 fetch 目的：  
 * 1. 请求开始时添加自定义请求头 traceid rpcid  
 * 2. 请求结束时上报请求信息  
 * @param uploader 日志上报方法
 * @param state 全局状态
 * @param opts 用户配置
 */
export function network(uploader: Function, state: LogState, opts: LogOptions) {
  hackXMLHttpRequest({
    beforeRequest(sharedData: SharedData, xhr: XMLHttpRequest) {
      setupSharedData(sharedData, state, opts)
      if (sharedData.shouldUrlIgnored || sharedData.shouldSampleDiscarded) return
      if (opts.trace.open) {
        // 添加自定义请求头，逻辑和重构前保持一致
        // setRequestHeader 方法必须在 open() 方法和 send() 之间调用。
        // https://developer.mozilla.org/zh-CN/docs/Web/API/XMLHttpRequest/setRequestHeader
        xhr.setRequestHeader('traceid', sharedData.traceid)
        xhr.setRequestHeader('rpcid', '1')
      }
    },
    afterRequest(sharedData: SharedData, xhr: XMLHttpRequest) {
      // 如果用户配置 stopAbortLog === true，且请求是被终止的，则不上报日志
      if (opts.network.stopAbortLog && sharedData.isAbort) return
      if (sharedData.shouldUrlIgnored || sharedData.shouldSampleDiscarded) return
      const { response, status, statusText } = xhr
      const data = buildAjaxLogData({ sharedData, initiatorType: 'xmlhttprequest', responseBody: response, status, statusText })
      if (!opts.network.stopAbortLog && sharedData.isAbort) {
        data.isAbort = 1
      }
      // 上报日志，逻辑和重构前保持一致
      uploader(data, 'c', 'network')
    },
  })

  hackFetch({
    beforeRequest(sharedData: SharedData, request: Request) {
      setupSharedData(sharedData, state, opts)
      if (sharedData.shouldUrlIgnored || sharedData.shouldSampleDiscarded) return
      if (opts.trace.open) {
        // 添加自定义请求头，逻辑和重构前保持一致
        request.headers.append('traceid', sharedData.traceid)
        request.headers.append('rpcid', '1')
      }
    },
    async afterRequest(sharedData: SharedData, response?: Response | null) {
      // 如果用户配置 stopAbortLog === true，且请求是被终止的，则不上报日志
      if (opts.network.stopAbortLog && sharedData.isAbort) return
      if (sharedData.shouldUrlIgnored || sharedData.shouldSampleDiscarded) return
      const { status, statusText } = response || {}
      const responseBody = await response?.text()
      const data = buildAjaxLogData({ sharedData, initiatorType: 'fetch', responseBody, status, statusText })
      if (!opts.network.stopAbortLog && sharedData.isAbort) {
        // 新增字段 isAbort，表示请求是否被终止了
        data.isAbort = 1
      }
      // 上报日志，逻辑和重构前保持一致
      uploader(data, 'c', 'network')
    },
  })
}

/**
 * 改写 window.XMLHttpRequest.prototype.open，支持配置发送前后回调函数，影响每一个 xhr 请求
 * @param options 
 */
export function hackXMLHttpRequest(options?: HackXMLHttpRequestOptions) {
  const { beforeRequest, afterRequest } = options || {}
  const originalOpen = window.XMLHttpRequest?.prototype.open

  if (typeof originalOpen === 'function') {
    window.XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null): void {
      url = getAbsoluteURL(url.toString())
      const sharedData: SharedData = { url, method }
      // 监听请求是否被终止，如果被终止，设置 sharedData.isAbort = 1
      const originalAbort = () => {
        sharedData.isAbort = 1
      }
      this.addEventListener('abort', originalAbort)
      this.addEventListener('readystatechange', () => {
        // readyState https://developer.mozilla.org/zh-CN/docs/Web/API/XMLHttpRequest/readyState
        // 请求即将开始 open() 方法已经被调用。send() 方法未被调用。
        if (this.readyState === 1) { beforeRequest?.(sharedData, this) }
        // 请求结束：数据传输已经彻底完成或失败。
        if (this.readyState === 4) {
          // readyState 为 4 表示请求已完成，响应已就绪。而 status 为 0 表示请求已被客户端（如通过调用 abort() 方法）或者服务器终止。
          if (this.status === 0) {
            // 为了保证 afterRequest 在 abort 事件之后执行，这里使用 setTimeout，0 毫秒后执行，相当于异步执行，这样会把 isAbort 的值传递给 afterRequest
            setTimeout(() => {
              afterRequest?.(sharedData, this)
              // 请求结束后，移除监听事件
              this.removeEventListener('abort', originalAbort)
            });
          } else {
            afterRequest?.(sharedData, this)
          }
        }
      })

      return originalOpen.apply(this, arguments)
    }
  }
}

/**
 * 改写 window.fetch，支持配置发送前后回调函数，影响每一个 fetch 请求
 * @param options 
 */
export function hackFetch(options?: HackFetchOptions) {
  const { beforeRequest, afterRequest } = options || {}
  const originalFetch = window.fetch

  if (typeof originalFetch === 'function') {
    window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // https://caniuse.com/mdn-api_request
      // https://developer.mozilla.org/zh-CN/docs/Web/API/Request
      const request = new Request(input, init)
      const { url, method } = request
      const sharedData: SharedData = { url, method }
      // 请求即将开始
      beforeRequest?.(sharedData, request)

      // 发请求
      const res: Promise<Response> = originalFetch.call(this, request)
      
      // 请求结束
      res.then(response => {
        // 请求成功
        // 不要直接使用 response 防止影响用户
        afterRequest?.(sharedData, response.clone())
        return response
      })
      // 不使用 finally 是因为以下浏览器版本中有 fetch 但没有 finally
      // Edge 14-18 Firefox 39-58 Chrome 42-63 Safari 10.1-11.1 
      // https://caniuse.com/promise-finally
      // https://caniuse.com/fetch
      .catch(reason => {
        // fetch请求如果被用户手动终止的，那么reason.name === 'AbortError'
        if (reason.name === 'AbortError') {
          sharedData.isAbort = 1
        }
        // 请求失败
        afterRequest?.(sharedData, null, reason)
        // 抛异常是为了不影响执行用户自己的捕获逻辑
        throw reason
      })

      return res
    }
  }
}

/**
 * 初始化共享数据，只在请求开始时调用一次
 * @param sharedData 异步请求开始前到结束后之间需要共享的数据
 * @param state 全局状态
 * @param opts 用户配置
 */
function setupSharedData(sharedData: SharedData, state: LogState, opts: LogOptions) {
  const { url } = sharedData
  sharedData.startTime = Date.now()
  // 重构后 traceid 和 ajaxid 的逻辑和以前保持一致
  sharedData.traceid = getTraceid(state)
  state.ajaxid += 1
  // 支持忽略特定 url
  sharedData.shouldUrlIgnored = isUrlIgnored(url, opts.ignoreAjaxFilter)
  // 支持采样率
  sharedData.shouldSampleDiscarded = isSampleDiscarded(opts.network.sample)  
}

/**
 * 把 xhr 或 fetch 的请求和响应信息转换成统一的数据结构上报，兼容重构前的所有字段
 * @param options 请求和响应信息
 * @returns 即将上报的日志信息
 */
function buildAjaxLogData(options: BuildAjaxLogDataOptions): Partial<Data> {
  const { sharedData, initiatorType, responseBody = '', status = -1, statusText = '' } = options || {}
  const { url, method, traceid = '', startTime } = sharedData
  return {
    type: isStatusSuccess(status) ? 'Performance-Ajax' : 'Ajax',
    resourceUrl: url,
    method,
    response: responseBody,
    status,
    statusText,
    effectiveType: navigator?.connection?.effectiveType || '',
    pageviewsize: getPageViewSize(),
    commitid,
    gitOriginUrl,
    performanceResourceTiming: getPerformanceResourceTiming(url, initiatorType),
    traceid,
    elapsedTime: Date.now() - startTime,
  }
}

/**
 * 通过响应状态码判断请求是否成功，逻辑和重构前保持一致
 * @param status 响应状态码
 * @returns 
 */
function isStatusSuccess(status?: number): boolean {
  return status && (status >= 200 && status < 300) || status === 304
}

/**
 * 获取特定请求的性能数据
 * @param name 请求 url
 * @param initiatorType 请求接口类型
 * @returns 
 */
function getPerformanceResourceTiming(name: string, initiatorType: 'xmlhttprequest' | 'fetch'): PerformanceResourceTiming | null {
  return (performance?.getEntriesByName?.(name) as PerformanceResourceTiming[]).filter(e => e.initiatorType === initiatorType)[0] || null
}

/**
 * 支持用户配置忽略处理特定 url 的请求
 * @param url 请求 url
 * @param urlignore 忽略的 url 列表
 * @returns 
 */
function isUrlIgnored(url: string, urlignore?: string[]): boolean {
  if (Array.isArray(urlignore)) {
    for (let i = 0; i < urlignore.length; i++) {
      if (url.indexOf(urlignore[i]) > -1) return true
    }
  }
  return false
}

/**
 * 支持用户配置采样率，随机决定是否丢弃样本
 * @param sampleRate 采样率
 */
function isSampleDiscarded(sampleRate?: number): boolean {
  // Math.random() 返回值 [0,1)（包括 0 不包括 1）
  return sampleRate && Math.random() >= sampleRate
}

/**
 * 获取请求唯一标识，逻辑和重构前保持一致，用于 http header 中的 traceid 和 http body 中的 content[n].data.traceid 
 */
function getTraceid(state: LogState) {
  return `${state.pvid || ''}-${state.ajaxid}`
}

/**
 * 相对 URL 转成绝对，例如 '/relative/path' => 'https://localhost/relative/path'
 * @param url 相对或绝对 URL
 */
function getAbsoluteURL(url: string): string {
  const base = document.baseURI
  url = url || ''
  // 较新的浏览器支持使用 URL 实例做转化 https://caniuse.com/mdn-api_url_href
  if (URL) return new URL(url, base).href
  // 以下逻辑为了兼容老浏览器
  // 已经是绝对的 URL 不必转换，不用正则判断是为了可读性好一点
  // 如果想支持 http 和 https 以外的协议就改用正则吧 https://stackoverflow.com/questions/161738/what-is-the-best-regular-expression-to-check-if-a-string-is-a-valid-url
  if (
    url.slice(0,7) === 'http://'
    || url.slice(0,8) === 'https://'
    || url.slice(0,2) === '//'
  ) return url
  // 相对 URL 拼接上 base URL
  return `${base}${url[0] === '/' ? url.slice(1) : url}`
}

/** 调用 hackXMLHttpRequest 改写 window.XMLHttpRequest.prototype.open 的参数 */
export interface HackXMLHttpRequestOptions {
  /**
   * 请求开始前回调函数
   * @param sharedData 异步请求开始前到结束后之间需要共享的数据
   * @param xhr XMLHttpRequest 实例
   */
  beforeRequest?(sharedData: SharedData, xhr: XMLHttpRequest): void
  /**
   * 请求结束后回调函数
   * @param sharedData 异步请求开始前到结束后之间需要共享的数据
   * @param xhr XMLHttpRequest 实例
   */
  afterRequest?(sharedData: SharedData, xhr: XMLHttpRequest): void
}

/** 调用 hackFetch 改写 window.fetch 的参数 */
export interface HackFetchOptions {
  /**
   * 请求开始前回调函数
   * @param sharedData 异步请求开始前到结束后之间需要共享的数据
   * @param request fetch(request) 请求参数实例
   */
  beforeRequest?(sharedData: SharedData, request: Request): void
  /**
   * 请求结束后回调函数
   * @description response 为 null 时表示出现异常了，执行了 catch 回调，没执行 then 回调
   * @param sharedData 异步请求开始前到结束后之间需要共享的数据
   * @param response fetch(request).then(response => {}) 响应实例
   * @param reason fetch(request).catch(reason => {}) 捕获到的异常原因
   */
  afterRequest?(sharedData: SharedData, response?: Response | null, reason?: any): void | Promise<void>
}

/** 转换参数，xhr 或 fetch 数据转成统一的数据结构上报 */
export interface BuildAjaxLogDataOptions {
  /** 异步请求开始前到结束后之间需要共享的数据 */
  sharedData: SharedData,
  /** 发请求使用的 API 类型， XMLHttpRequest 或 fetch */
  initiatorType: 'xmlhttprequest' | 'fetch',
  /** 响应 body 内容 */
  responseBody?: string,
  /** 响应状态码 */
  status?: number,
  /** 响应状态文本 */
  statusText?: string,
}

/**
 * 异步请求开始前到结束后之间需要共享的数据
 */
export interface SharedData {
  /** 请求 URL */
  url: string,
  /** 请求方法 */
  method: string,
  /** 是否忽略当前 url 的请求 */
  shouldUrlIgnored?: boolean,
  /** 是否丢弃当前请求样本 */
  shouldSampleDiscarded?: boolean,
  /** 请求开始时间 */
  startTime?: number,
  /** 请求唯一标识 */
  traceid?: string,
  /** 是否是手动终止的请求 */
  isAbort?: number,
}
